import { Form, Image, Select, Upload } from "antd";
import { ReactElement, useEffect, useState } from "react";
import { InboxOutlined } from "@ant-design/icons";

import Label from "../../components/label/label.component";
import "./add-edit-document.scss";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AxiosResponse } from "axios";
import instance from "../../store/axios";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import {
  ADD_TO_BREADCRUMB,
  LOADING,
  REMOVE_FROM_BREADCRUMB,
} from "../../store/actions";
import {
  getStandingDataOptions,
  isImageFile,
} from "../../utils/helper/functions";
import CollapseComponent from "../../components/collapse.component";
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import CustomModal from "src/components/custom-modal/custom-modal";
import PdfViewer from "src/components/pdf-viewer";
import CustomInput from "src/components/custom-input/custom-input.component";
import LoadingComponent from "src/components/loading/loading.component";

const { Dragger } = Upload;

const AddEditDocument: React.FC = (): ReactElement => {
  const { accountId, activeKyc } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isApproveCase = queryParams.get("approve");
  const docType = localStorage.getItem("docType") as any;
  const storagePath = localStorage.getItem("storagePath") as any;
  const documentExtension = localStorage.getItem("documentExtension") as any;
  const [form] = Form.useForm();
  const [standingData, setStandingData] = useState<any>();
  const [openPdfViewerModal, setOpenPdfViewerModal] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<any>({});
  const [accountKycDetails, setAccountKycDetails] = useState<any>();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    getStandindData();
    getKycApi();
    dispatch({
      type: ADD_TO_BREADCRUMB,
      payload: {
        breadcrumb: [
          {
            text: `Update document details`,
            type: "text",
          },
        ],
      },
    });

    return () => {
      dispatch({
        type: REMOVE_FROM_BREADCRUMB,
        payload: {
          breadcrumb: [
            {
              text: `Update document details`,
            },
          ],
        },
      });
    };
  }, []);

  const getStandindData = async (): Promise<void> => {
    setLoading(true);
    try {
      const response: AxiosResponse<any> = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/option/standing-data`
      );
      setStandingData(response?.data?.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/document/${accountId}/${activeKyc}/edit`
      );
    } finally {
      setLoading(false);
    }
  };

  const getKycApi = async (): Promise<void> => {
    setLoading(true);

    try {
      const response: AxiosResponse = await instance.get(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/account/${accountId}?kycId=${activeKyc}`
      );
      setAccountKycDetails(response?.data?.data);
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
    }

    setLoading(false);
  };

  const onSubmit = async (values: any) => {
    dispatch({ type: LOADING, payload: true });
    const formData = new FormData();
    formData.append("file", values.file.file);
    formData.append("documentType", docType || values.type);
    formData.append("branch", "KW");

    if (isApproveCase) {
      formData.append("isPendingUpload", "true");
    }

    if (values.description) {
      formData.append("otherDocDescription", values.description);
    }
    if (values.name) {
      formData.append("otherDocName", values.name);
    }
    try {
      await instance.post(
        `/${process.env.REACT_APP_ENDPOINT_VERSION}/files/${accountId}/upload`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      docType
        ? toast.success("Document updated successfully")
        : toast.success("document added successfully");
      navigate(
        `/home/business-account/${accountId}/${activeKyc}/details?tab=2${
          isApproveCase ? "&approve=1" : ""
        }`
      );
    } catch (error: any) {
      if (error?.response?.status !== 401) {
        toast.error(error?.response?.data?.message || "An error occurred");
      }
      localStorage.setItem(
        "star_prev_url",
        `/document/${accountId}/${activeKyc}/edit${
          isApproveCase ? "?approve=1" : ""
        }`
      );
    }
    dispatch({ type: LOADING, payload: false });
  };

  const beforeUpload = (file) => {
    const isImageOrPdf =
      file.type?.toLowerCase() === "image/png" ||
      file.type?.toLowerCase() === "image/gif" ||
      file.type?.toLowerCase() === "image/jpg" ||
      file.type?.toLowerCase() === "image/jpeg" ||
      file.type?.toLowerCase() === "application/pdf";

    if (!isImageOrPdf) {
      toast.error(
        "Error: Only PDF and image files are allowed for upload. Please try again with a valid file format!"
      );
    }

    if (!isImageOrPdf) {
      return Upload.LIST_IGNORE;
    }
    return false;
  };

  return loading ? (
    <LoadingComponent />
  ) : (
    <div className="add-edit-document-container flex flex-col">
      <BreadcrumbComponent />
      <h3>
        {
          standingData?.DocumentType?.find((type: any) => type.id === +docType)
            ?.description
        }
      </h3>
      {standingData && (
        <Form
          form={form}
          className="add-edit-form"
          onFinish={onSubmit}
          initialValues={{ type: +docType || null }}
        >
          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-lg font-semibold">Document details</h3>
              </div>
            }
          >
            <div className="field w-full lg:w-1/2 flex flex-col">
              <Label text="Doc. type" />
              <Form.Item
                name="type"
                rules={[
                  {
                    required: true,
                    message: "Please select the document type!",
                  },
                ]}
              >
                <Select
                  placeholder="Select Document"
                  allowClear
                  showSearch
                  disabled={docType ? true : false}
                  filterOption={(input, option: any) => {
                    return (option?.key?.toLowerCase() ?? "").includes(
                      input.toLowerCase()
                    );
                  }}
                  options={[
                    ...getStandingDataOptions(
                      standingData,
                      "DocumentType",
                      accountKycDetails?.branch === "SA"
                        ? ["sa", "gc"]
                        : ["kw", "gc"],
                      false
                    ),
                  ]}
                />
              </Form.Item>
            </div>

            <Form.Item
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.type !== currentValues.type
              }
            >
              {({ getFieldValue }) =>
                getFieldValue("type") === 196 ? (
                  <div className="field w-full lg:w-1/2 flex flex-col">
                    <Label text="Doc. name" />
                    <Form.Item
                      name="name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter the document name!",
                        },
                      ]}
                      noStyle
                    >
                      <CustomInput placeholder="Enter the document name" />
                    </Form.Item>
                  </div>
                ) : null
              }
            </Form.Item>

            <Form.Item
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.type !== currentValues.type
              }
            >
              {({ getFieldValue }) =>
                getFieldValue("type") === 196 ? (
                  <div className="field w-full lg:w-1/2 flex flex-col">
                    <Label text="Doc. description" />
                    <Form.Item
                      name="description"
                      rules={[
                        {
                          required: true,
                          message: "Please enter the document description!",
                        },
                      ]}
                    >
                      <CustomInput placeholder="Enter the document description" />
                    </Form.Item>
                  </div>
                ) : null
              }
            </Form.Item>

            <div className="fields flex w-full lg:w-1/2 upload-container">
              <Form.Item
                name="file"
                rules={[
                  {
                    required: true,
                    message: "Please upload the file",
                  },
                ]}
                className="w-full"
              >
                <Dragger
                  beforeUpload={beforeUpload}
                  customRequest={() => false}
                  multiple={false}
                  accept="image/*,.pdf"
                  maxCount={1}
                  className="w-full"
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">
                    Click or drag file to this area to upload
                  </p>
                </Dragger>
              </Form.Item>
            </div>

            {isImageFile(documentExtension) ? (
              <Image
                src={storagePath}
                width={"80px"}
                height={"50px"}
                alt="kamco document"
              />
            ) : (
              <span
                className="underline cursor-pointer text-blue-500"
                onClick={() => {
                  setSelectedDocument(storagePath);
                  setOpenPdfViewerModal(true);
                }}
              >
                {
                  standingData?.DocumentType?.find(
                    (type: any) => type.id === +docType
                  )?.description
                }
              </span>
            )}
          </CollapseComponent>

          <div className="flex w-full flex-wrap items-center justify-end mt-7 pt-5">
            <div className="flex justify-between items-center w-[70%] md:w-[48%]">
              <button
                onClick={() =>
                  navigate(
                    `/home/business-account/${accountId}/${activeKyc}/details?tab=2${
                      isApproveCase ? "&approve=1" : ""
                    }`
                  )
                }
                className="rounded-lg bg-white w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
              >
                Cancel
              </button>
              <button className="disabled:opacity-50 disabled:cursor-not-allowed rounded-lg bg-blue-500 w-[48%] py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white">
                Save
              </button>
            </div>
          </div>
        </Form>
      )}

      <CustomModal
        isVisible={openPdfViewerModal}
        centered
        width={1100}
        showClose={false}
        onClose={() => {
          setOpenPdfViewerModal(false);
        }}
        className="edit-modal"
      >
        <div className="flex flex-col items-center justify-center">
          <PdfViewer fileUrl={selectedDocument} />
        </div>
      </CustomModal>
    </div>
  );
};

export default AddEditDocument;
